/**
 * AASB 119 — Employee Benefits. Rulepack v1.0.0.
 * Validates leave provisions (annual + long service) per employee.
 *
 * Out of scope: defined-benefit superannuation, post-employment medical,
 * termination benefits beyond simple severance — these need actuarial inputs.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parsePercent, parseDate, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb119-1.0.0';
const fb = findingBuilder('AASB_119' as any, VERSION);

const HMAP = makeHeaderMap([
  ['employee_id', 'employee id', 'staff id', 'emp id'],
  ['base_salary', 'base salary', 'salary', 'annual salary'],
  ['annual_leave_days_balance', 'annual leave', 'al days', 'annual leave days', 'al balance', 'annual leave days balance', 'al days balance'],
  ['lsl_balance_weeks', 'lsl', 'long service leave', 'lsl weeks', 'lsl balance', 'lsl balance weeks', 'long service leave weeks'],
  ['leave_loading_pct', 'leave loading', 'loading', 'al loading'],
  ['annual_leave_provision', 'al provision', 'annual leave provision'],
  ['lsl_provision', 'lsl provision'],
  ['hire_date', 'hire date', 'start date', 'commencement'],
  ['as_at_date', 'as at', 'as at date'],
]);

const REQUIRED = ['employee_id', 'base_salary', 'annual_leave_days_balance', 'lsl_balance_weeks'];
const WORK_DAYS_PER_YEAR = 261;

export function detectAASB119(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  return { confidence: matched.length / REQUIRED.length, matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB119(headers: string[], data: CellValue[][], asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_119' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('employee_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB119.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const salary = parseNumber(get('base_salary'));
    const alDays = parseNumber(get('annual_leave_days_balance'));
    const lslWeeks = parseNumber(get('lsl_balance_weeks'));
    const loadingPct = idx.leave_loading_pct !== undefined ? (parsePercent(get('leave_loading_pct')) ?? 0) : 0;
    const reportedAL = idx.annual_leave_provision !== undefined ? parseNumber(get('annual_leave_provision')) : null;
    const reportedLSL = idx.lsl_provision !== undefined ? parseNumber(get('lsl_provision')) : null;
    const hireDate = idx.hire_date !== undefined ? parseDate(get('hire_date')) : null;

    if (!(salary !== null && salary > 0)) { findings.push(fb.build('AASB119.R02', 'error', 'Salary must be positive', `Employee ${id}: salary ${salary}.`, { rowId: id })); return; }
    if (alDays! < 0 || lslWeeks! < 0) { findings.push(fb.build('AASB119.R03', 'error', 'Leave balances cannot be negative', `Employee ${id}: AL ${alDays}, LSL ${lslWeeks}.`, { rowId: id })); return; }

    // R04 AL provision recalc: daily rate × days × (1 + loading)
    const dailyRate = salary! / WORK_DAYS_PER_YEAR;
    const expectedAL = dailyRate * alDays! * (1 + loadingPct);
    if (reportedAL !== null) {
      const tol = Math.max(50, 0.01 * salary!);
      const delta = reportedAL - expectedAL;
      if (Math.abs(delta) > tol) {
        const sev: Finding['severity'] = Math.abs(delta) > 3 * tol ? 'error' : 'warning';
        findings.push(fb.build('AASB119.R04', sev, 'Annual leave provision differs from recalc',
          `Employee ${id}: reported $${fmt(reportedAL)}, expected $${fmt(expectedAL)} (${alDays} days × $${fmt(dailyRate)} × (1 + ${(loadingPct * 100).toFixed(1)}% loading)). Delta ${delta >= 0 ? '+' : '−'}$${fmt(Math.abs(delta))}.`,
          { rowId: id, evidence: { reported: reportedAL, expected: expectedAL, alDays, dailyRate, loadingPct }, suggestedAdjustment: { field: 'annual_leave_provision', from: reportedAL, to: +expectedAL.toFixed(2), rationale: 'Daily rate × balance days × (1 + loading).' } }));
      }
    }

    // R05 LSL recalc: weeks × weekly rate (simplified — full AASB 119 requires PV + service-based vesting)
    const weeklyRate = salary! / 52;
    const expectedLSL = weeklyRate * lslWeeks!;
    if (reportedLSL !== null) {
      const tol = Math.max(100, 0.02 * salary!);
      if (Math.abs(reportedLSL - expectedLSL) > tol) {
        findings.push(fb.build('AASB119.R05', 'info', 'LSL provision differs from simple recalc',
          `Employee ${id}: reported $${fmt(reportedLSL)}, simple recalc $${fmt(expectedLSL)} (${lslWeeks} weeks × $${fmt(weeklyRate)}). Difference may be intentional — full AASB 119 LSL requires present-value adjustment for non-vesting portions and assumed wage growth. This rulepack uses a simplified undiscounted recalc.`,
          { rowId: id, evidence: { reported: reportedLSL, simpleRecalc: expectedLSL } }));
      }
    }

    // R06 excessive AL balance — flag for review (cashflow + Fair Work fairness)
    if (alDays! > 40) {
      findings.push(fb.build('AASB119.R06', 'info', 'High annual leave balance',
        `Employee ${id}: ${alDays} days AL on hand. Many employers have a directive to keep <40 days; consider Fair Work s93 directive option.`,
        { rowId: id, evidence: { alDays } }));
    }

    // R07 LSL eligibility (AU rule of thumb — varies by state, often 7–10 years)
    if (hireDate) {
      const yearsService = (asAt.getTime() - hireDate.getTime()) / (365.25 * 86400000);
      if (yearsService < 7 && lslWeeks! > 0) {
        findings.push(fb.build('AASB119.R07', 'info', 'LSL balance with <7 years service',
          `Employee ${id}: ${yearsService.toFixed(1)} years service, LSL balance ${lslWeeks} weeks. State-based LSL accrual rules vary — confirm pro-rata accrual is appropriate.`,
          { rowId: id, evidence: { yearsService } }));
      }
    }
  });

  return {
    summary: { standard: 'AASB_119' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
